import {
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  TextFieldProps,
  useTheme,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { debounce } from 'lodash';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WbDivider } from '../WbDivider';
import { WbButton } from '../WbButton';
import clsx from 'clsx';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';

const useStyles = makeStyles(
  theme => ({
    root: {
      backgroundColor: theme.palette.white,
      height: 'auto',
      borderRadius: 4,
    },
    input: {
      paddingRight: theme.spacing(1),
    },
    srOnly: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
  }),
  { name: 'WbSearch' },
);

export type WbSearchProps = Omit<TextFieldProps, 'onChange'> & {
  debounced?: boolean;
  onClear?: () => void;
  onChange?: (value: string) => void;
  submit?: () => void;
  submitButton?: boolean;
  inputHeight?: string | number;
  inputClassName?: string;
  hasAutocomplete?: boolean;
  label?: string;
  // enabled by default, disable only when providing a custom label from outside using the id
  provideAriaLabel?: boolean;
};

const FALLBACK_LABEL = 'Search';

export const WbSearch = ({
  onChange,
  value,
  debounced,
  placeholder,
  submit,
  submitButton,
  onClear,
  inputHeight,
  inputClassName,
  hasAutocomplete = false,
  label = FALLBACK_LABEL,
  provideAriaLabel = true,
  ...props
}: WbSearchProps) => {
  const theme = useTheme();
  const classes = useStyles();
  const [search, setSearch] = useState(value ?? '');
  const hintId = useId();
  const inputRef = useRef<HTMLInputElement>();

  const showFullLabel =
    (useApi(configApiRef)?.getOptionalBoolean('mesh.showSearchFullLabel') ??
      false) &&
    label;

  const inputPropsWithStyle = {
    ...props.InputProps,
  };

  // Workaround to clear input when user clear all filters
  useEffect(() => {
    if (value === '' || value === undefined) setSearch('');
  }, [value]);

  const handleDebounceFn = useCallback(
    (v: string) => {
      onChange?.(v);
    },
    [onChange],
  );

  const debounceFn = useMemo(
    () => debounce(handleDebounceFn, 500),
    [handleDebounceFn],
  );

  const handleChangeSearch = (e: any) => {
    const newValue = e.currentTarget.value;
    setSearch(newValue);
    if (debounced) debounceFn(newValue);
    else handleDebounceFn(newValue);
  };

  const handleClearSearch = () => {
    onChange?.('');
    setSearch('');
    onClear?.();
    inputRef.current?.focus();
  };

  const enterButton = (
    <WbButton
      type="submit"
      iconColor="white"
      icon={<SearchIcon />}
      aria-label="Search"
      onClick={submit}
      style={{ color: theme.palette.primary.main }}
    />
  );

  const clearButtonVisible = Boolean(search);
  const searchId = useId();

  const content = (
    <>
      {hasAutocomplete && (
        <span id={hintId} className={classes.srOnly}>
          Search suggestions will appear below as you type
        </span>
      )}
      <TextField
        label={showFullLabel ? label : undefined}
        inputRef={inputRef}
        fullWidth
        variant="outlined"
        id={searchId}
        size="small"
        classes={{ root: classes.root }}
        value={search ?? ''}
        onChange={handleChangeSearch}
        placeholder={placeholder ?? label}
        {...props}
        InputLabelProps={{
          shrink: true,
          style: {
            color: theme.palette.black,
            textTransform: 'uppercase',
            fontWeight: 700,
            fontSize: theme.typography.caption.fontSize,
          },
        }}
        InputProps={{
          ...inputPropsWithStyle,
          className: clsx(classes.input, inputClassName),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                style={{
                  visibility: clearButtonVisible ? undefined : 'hidden',
                }}
                type="button"
                aria-label="Clear search"
                onClick={handleClearSearch}
                disabled={!search}
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
              {clearButtonVisible && (
                <WbDivider orientation="vertical" style={{ margin: '4px' }} />
              )}
              {submitButton ? enterButton : <SearchIcon fontSize="small" />}
            </InputAdornment>
          ),
        }}
        inputProps={{
          ...props.inputProps,
          // if showFullLabel is true, a label for the input is provided already by the element label, no need for aria-label
          'aria-label':
            !showFullLabel && provideAriaLabel
              ? placeholder ?? label
              : undefined,
          'aria-describedby': hasAutocomplete ? hintId : undefined,
        }}
      />
    </>
  );

  return submit ? (
    <form
      style={{ display: 'contents' }}
      onSubmit={
        submit
          ? e => {
              e.preventDefault();
              submit();
            }
          : undefined
      }
    >
      {content}
    </form>
  ) : (
    content
  );
};
